import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Politique de confidentialité - Anti-Recouvrement.com</title>
        <meta name="description" content="Politique de confidentialité et protection des données personnelles d'Anti-Recouvrement.com" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 py-4">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center text-blue-900 hover:text-blue-700">
              <ArrowLeft size={20} className="mr-2" />
              Retour à l'accueil
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#1e3a8a' }}>
            Politique de confidentialité
          </h1>

          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700">
                La société <strong>Sequana</strong> (ci-après "nous" ou "notre") accorde une grande importance à la protection
                de vos données personnelles. Cette politique de confidentialité vous informe sur la manière dont nous collectons,
                utilisons et protégeons vos informations lorsque vous utilisez le site <strong>anti-recouvrement.com</strong>.
              </p>
            </section>

            {/* Responsable du traitement */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">1. Responsable du traitement</h2>
              <div className="text-gray-700 space-y-2">
                <p>Le responsable du traitement des données personnelles est :</p>
                <ul className="list-none space-y-1 mt-4">
                  <li><strong>Sequana SASU</strong></li>
                  <li>4 rue du Général Roux, 77590 Fontaine-le-Port, France</li>
                  <li>Email : hello@sequana.tech</li>
                  <li>SIRET : 978 347 540 00020</li>
                </ul>
              </div>
            </section>

            {/* Données collectées */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">2. Données personnelles collectées</h2>
              <p className="text-gray-700 mb-4">Nous collectons les données suivantes :</p>

              <h3 className="font-semibold text-gray-800 mt-4 mb-2">Données fournies volontairement :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Documents téléchargés (courriers de recouvrement)</li>
              </ul>

              <h3 className="font-semibold text-gray-800 mt-4 mb-2">Données collectées automatiquement :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Adresse IP</li>
                <li>Type de navigateur et système d'exploitation</li>
                <li>Pages visitées et durée de visite</li>
                <li>Données de cookies (voir section dédiée)</li>
              </ul>
            </section>

            {/* Finalités */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">3. Finalités du traitement</h2>
              <p className="text-gray-700 mb-4">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Fournir notre service d'analyse de documents de recouvrement</li>
                <li>Créer et gérer votre compte utilisateur</li>
                <li>Vous contacter concernant votre dossier</li>
                <li>Traiter vos paiements</li>
                <li>Améliorer notre service et votre expérience utilisateur</li>
                <li>Respecter nos obligations légales</li>
                <li>Établir des statistiques anonymisées</li>
              </ul>
            </section>

            {/* Base légale */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">4. Base légale du traitement</h2>
              <p className="text-gray-700 mb-4">Le traitement de vos données repose sur :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>L'exécution du contrat :</strong> pour fournir le service que vous avez demandé</li>
                <li><strong>Votre consentement :</strong> pour l'envoi de communications marketing</li>
                <li><strong>Notre intérêt légitime :</strong> pour améliorer nos services et prévenir la fraude</li>
                <li><strong>Obligations légales :</strong> pour respecter la réglementation applicable</li>
              </ul>
            </section>

            {/* Destinataires */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">5. Destinataires des données</h2>
              <p className="text-gray-700 mb-4">Vos données peuvent être partagées avec :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Nos sous-traitants techniques :</strong> hébergement (OVH), analyse IA, paiement (Stripe), envoi d'emails (Brevo)</li>
                <li><strong>Avocats partenaires spécialisés en droit du recouvrement :</strong> sous réserve de votre consentement explicite et confirmé par email, vos coordonnées (nom, email, téléphone) ainsi que les éléments synthétiques de votre dossier peuvent être transmis à un avocat partenaire afin qu'il vous propose une consultation. Aucun document brut n'est transmis avant cette consultation.</li>
                <li><strong>Les autorités compétentes :</strong> en cas d'obligation légale</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>Base légale du partage avec les avocats partenaires :</strong> votre consentement, recueilli explicitement au moment de la soumission de votre dossier via une case à cocher dédiée (non pré-cochée). La preuve de ce consentement est conservée (date, adresse IP). Vous pouvez retirer votre consentement à tout moment en nous contactant — voir section « Vos droits » ci-dessous.
              </p>
            </section>

            {/* Durée de conservation */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">6. Durée de conservation</h2>
              <p className="text-gray-700 mb-4">Vos données sont conservées pendant :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Données de compte :</strong> 3 ans après votre dernière activité</li>
                <li><strong>Documents analysés :</strong> 1 an après l'analyse, sauf demande de suppression</li>
                <li><strong>Données de facturation :</strong> 10 ans (obligation légale)</li>
                <li><strong>Cookies analytiques :</strong> 13 mois maximum</li>
              </ul>
            </section>

            {/* Sécurité */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">7. Sécurité des données</h2>
              <p className="text-gray-700">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données
                contre tout accès non autorisé, modification, divulgation ou destruction :
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-4 space-y-1">
                <li>Chiffrement SSL/TLS des communications</li>
                <li>Stockage sécurisé des documents</li>
                <li>Accès restreint aux données personnelles</li>
                <li>Surveillance et audits réguliers</li>
              </ul>
            </section>

            {/* Droits */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">8. Vos droits</h2>
              <p className="text-gray-700 mb-4">
                Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Droit d'accès :</strong> obtenir une copie de vos données</li>
                <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> supprimer vos données ("droit à l'oubli")</li>
                <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
                <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                <li><strong>Droit de retirer votre consentement :</strong> à tout moment pour les traitements basés sur le consentement</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Pour exercer ces droits, contactez-nous à : <strong>hello@sequana.tech</strong>
              </p>
              <p className="text-gray-700 mt-2">
                Vous pouvez également introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) :
                www.cnil.fr
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">9. Cookies</h2>
              <p className="text-gray-700 mb-4">Notre site utilise des cookies pour :</p>

              <h3 className="font-semibold text-gray-800 mt-4 mb-2">Cookies essentiels (toujours actifs) :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Authentification et sécurité de session</li>
                <li>Mémorisation de vos préférences</li>
              </ul>

              <h3 className="font-semibold text-gray-800 mt-4 mb-2">Cookies analytiques (avec consentement) :</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Google Analytics 4 : mesure d'audience et amélioration du service</li>
              </ul>

              <p className="text-gray-700 mt-4">
                Vous pouvez gérer vos préférences de cookies à tout moment via les paramètres de votre navigateur.
              </p>
            </section>

            {/* Transferts internationaux */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">10. Transferts internationaux</h2>
              <p className="text-gray-700">
                Certains de nos sous-traitants peuvent être situés en dehors de l'Union Européenne (notamment pour les services
                d'analyse IA). Dans ce cas, nous nous assurons que des garanties appropriées sont mises en place
                (clauses contractuelles types, certification Privacy Shield, etc.) conformément à la réglementation applicable.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">11. Modifications de la politique</h2>
              <p className="text-gray-700">
                Nous pouvons mettre à jour cette politique de confidentialité à tout moment. En cas de modification substantielle,
                nous vous en informerons par email ou via une notification sur le site.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">12. Contact</h2>
              <p className="text-gray-700">
                Pour toute question concernant cette politique ou vos données personnelles, contactez-nous :
              </p>
              <ul className="list-none space-y-1 mt-4 text-gray-700">
                <li><strong>Email :</strong> hello@sequana.tech</li>
                <li><strong>Adresse :</strong> Sequana SASU, 4 rue du Général Roux, 77590 Fontaine-le-Port, France</li>
              </ul>
            </section>

            {/* Date de mise à jour */}
            <section className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-6 mt-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-gray-500 text-sm">
              © {new Date().getFullYear()} Anti-Recouvrement.com — Sequana SASU
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
